/**
 * ModuleQuizCompletedStage Component
 *
 * 모듈 마무리 퀴즈 완료 전용 화면
 * 3가지 상태: 통과(PASSED), 실패(FAILED), 재도전완료(RE_COMPLETED)
 * - 상태별 타이틀, 안내 멘트, 아이콘
 * - 현재/이전 결과 표시
 * - 상태별 액션 버튼
 */

import { useTranslation } from 'react-i18next'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import {
    Trophy,
    CheckCircle2,
    RotateCcw,
    ChevronRight,
    ArrowLeft,
    BookOpen,
    FileText,
    Loader2,
    List,
    Sparkles,
} from 'lucide-react'
import { learningSessionApi, type ModuleQuizCompletionSummaryResponse } from '@/api/learningSession'
import { contentApi } from '@/api/content'

// ============================================================================
// Types
// ============================================================================

type ModuleQuizCompletionState = 'PASSED' | 'FAILED' | 'RE_COMPLETED'

export interface ModuleQuizCompletedStageProps {
    /** 모듈 ID */
    moduleId: number
    /** 학습자 이름 */
    learnerName?: string
    /** B2B 클래스 상품 ID */
    classProductId?: number | null
    /** 채점 결과 보기 (퀴즈 리뷰 모드 진입) */
    onViewResults: (sessionId: number, componentProgressId: number) => void
    /** 다음 레슨으로 이동 */
    onNextLesson: (lessonId: number) => void
    /** 복습하기 (모듈 첫 레슨으로 이동) */
    onReview: (lessonId: number) => void
    /** 재도전 (ModuleOverview로 이동) */
    onRetry: () => void
    /** 모듈 개요로 이동 */
    onBackToModule: () => void
    /** 수강 완료 확인 (Post-AIQ 안내) */
    onCourseComplete: () => void
    /** Post-AIQ 응시 가능 여부 (학습 완료 시 true) */
    canStartPostAiq?: boolean
    /** Post-AIQ 이미 완료 여부 */
    postAiqCompleted?: boolean
    /** AIQ 페이지로 이동 핸들러 */
    onNavigateToAiq?: () => void
    /** Optional className */
    className?: string
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * 상태 판별
 */
function determineState(
    passed: boolean,
    hadPreviouslyPassed: boolean
): ModuleQuizCompletionState {
    if (!passed) {
        return 'FAILED'
    }
    if (hadPreviouslyPassed) {
        return 'RE_COMPLETED'
    }
    return 'PASSED'
}

/**
 * 날짜 포맷 (yyyy.mm.dd hh:mm)
 */
function formatDateTime(isoString: string): string {
    const date = new Date(isoString)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${year}.${month}.${day} ${hours}:${minutes}`
}

/**
 * 정답률 포맷 (소수점 1자리)
 */
function formatScore(score: number): string {
    return `${score.toFixed(1)}%`
}

// ============================================================================
// Component
// ============================================================================

export function ModuleQuizCompletedStage({
    moduleId,
    learnerName,
    classProductId,
    onViewResults,
    onNextLesson,
    onReview,
    onRetry,
    onBackToModule,
    onCourseComplete,
    canStartPostAiq,
    postAiqCompleted,
    onNavigateToAiq,
    className,
}: ModuleQuizCompletedStageProps) {
    const { t } = useTranslation('learner')
    const displayLearnerName = learnerName || t('learning.moduleQuizCompleted.defaultLearner')
    // API 조회: 퀴즈 완료 요약
    const { data: summary, isLoading: isLoadingSummary, error: summaryError } = useQuery({
        queryKey: ['moduleQuizCompletionSummary', moduleId, classProductId],
        queryFn: () => learningSessionApi.getModuleQuizCompletionSummary(moduleId, classProductId),
        staleTime: 30 * 1000,
    })

    // API 조회: 모듈 상세 (첫 번째 레슨 ID 조회용)
    const { data: moduleDetail, isLoading: isLoadingModule } = useQuery({
        queryKey: ['moduleDetail', moduleId],
        queryFn: () => contentApi.getModuleDetail(moduleId),
        staleTime: 5 * 60 * 1000,
    })

    // 첫 번째 레슨 ID
    const firstLessonId = moduleDetail?.lessons?.[0]?.id

    // 로딩 상태
    if (isLoadingSummary || isLoadingModule) {
        return (
            <div className="flex justify-center items-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        )
    }

    // 에러 상태
    if (summaryError || !summary) {
        return (
            <div className="py-20 text-center">
                <p className="text-gray-500">{t('learning.moduleQuizCompleted.loadFailed')}</p>
                <button
                    onClick={onBackToModule}
                    className="mt-4 text-primary-600 hover:text-primary-700"
                >
                    {t('learning.moduleQuizCompleted.backToModule')}
                </button>
            </div>
        )
    }

    // 상태 판별
    const state = determineState(
        summary.currentSession.passed,
        summary.hadPreviouslyPassed
    )

    return (
        <div className={cn(
            'px-4 py-8 mx-auto max-w-2xl duration-300 animate-in fade-in zoom-in-95',
            className
        )}>
            {/* Icon */}
            <StateIcon state={state} />

            {/* Title & Message */}
            <StateMessage
                state={state}
                learnerName={displayLearnerName}
                moduleTitle={summary.moduleTitle}
                t={t}
            />

            {/* Results Card */}
            <ResultsCard
                currentSession={summary.currentSession}
                previousSession={summary.previousSession}
                t={t}
            />

            {/* View Results Button: 통과 시, 실패했지만 이전에 통과한 적 있으면(completed), DEV 환경 */}
            {(summary.currentSession.passed || summary.hadPreviouslyPassed || import.meta.env.DEV) && (
                <div className="flex justify-center mb-6">
                    <button
                        onClick={() => onViewResults(
                            summary.currentSession.sessionId,
                            summary.currentSession.componentProgressId
                        )}
                        className="flex items-center gap-2 px-5 py-2.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg font-medium transition-colors"
                    >
                        <FileText className="w-5 h-5" />
                        <span>{t('learning.moduleQuizCompleted.viewResults')}</span>
                    </button>
                </div>
            )}

            {/* Post-AIQ Guidance Card (코스 완료 + 통과 + Post-AIQ 응시 가능) */}
            {summary.isCourseCompleted && state !== 'FAILED' && canStartPostAiq && !postAiqCompleted && (
                <div className="flex flex-col gap-3 items-center p-5 mb-6 bg-blue-50 rounded-2xl border border-blue-200">
                    <div className="flex gap-2 items-center text-blue-700">
                        <Sparkles className="w-5 h-5" />
                        <span className="font-semibold">{t('learning.moduleQuizCompleted.postAiqAvailable')}</span>
                    </div>
                    <p className="text-sm text-center text-blue-600">
                        {t('learning.moduleQuizCompleted.postAiqAvailableDesc')}
                    </p>
                    {onNavigateToAiq && (
                        <button
                            onClick={onNavigateToAiq}
                            className="flex gap-2 items-center px-6 py-2.5 font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
                        >
                            <Sparkles className="w-4 h-4" />
                            {t('learning.moduleQuizCompleted.goToPostAiq')}
                        </button>
                    )}
                </div>
            )}

            {/* Post-AIQ 완료 안내 */}
            {summary.isCourseCompleted && postAiqCompleted && (
                <div className="flex gap-3 items-center p-4 mb-6 bg-emerald-50 rounded-xl border border-emerald-200">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    <p className="text-sm font-medium text-emerald-700">
                        {t('learning.moduleQuizCompleted.postAiqDone')}
                    </p>
                </div>
            )}

            {/* Action Buttons */}
            <ActionButtons
                state={state}
                summary={summary}
                firstLessonId={firstLessonId}
                onNextLesson={onNextLesson}
                onReview={onReview}
                onRetry={onRetry}
                onBackToModule={onBackToModule}
                onCourseComplete={onCourseComplete}
                t={t}
            />
        </div>
    )
}

// ============================================================================
// Sub Components
// ============================================================================

interface StateIconProps {
    state: ModuleQuizCompletionState
}

function StateIcon({ state }: StateIconProps) {
    const iconConfig = {
        PASSED: {
            bgClass: 'bg-gradient-to-br from-emerald-400 to-emerald-600',
            Icon: Trophy,
        },
        FAILED: {
            bgClass: 'bg-gradient-to-br from-amber-400 to-amber-600',
            Icon: RotateCcw,
        },
        RE_COMPLETED: {
            bgClass: 'bg-gradient-to-br from-blue-400 to-blue-600',
            Icon: CheckCircle2,
        },
    }

    const { bgClass, Icon } = iconConfig[state]

    return (
        <div className="flex justify-center mb-6">
            <div className={cn(
                'flex justify-center items-center w-24 h-24 rounded-full shadow-lg',
                bgClass
            )}>
                <Icon className="w-14 h-14 text-white" />
            </div>
        </div>
    )
}

interface StateMessageProps {
    state: ModuleQuizCompletionState
    learnerName: string
    moduleTitle: string
    t: (key: string, opts?: Record<string, unknown>) => string
}

function StateMessage({ state, learnerName, moduleTitle, t }: StateMessageProps) {
    const messages = {
        PASSED: {
            title: t('learning.moduleQuizCompleted.passedTitle'),
            lines: [
                t('learning.moduleQuizCompleted.passedLine1', { name: learnerName }),
                t('learning.moduleQuizCompleted.passedLine2', { module: moduleTitle }),
            ],
        },
        FAILED: {
            title: t('learning.moduleQuizCompleted.failedTitle'),
            lines: [
                t('learning.moduleQuizCompleted.failedLine1', { name: learnerName }),
                t('learning.moduleQuizCompleted.failedLine2', { module: moduleTitle }),
                t('learning.moduleQuizCompleted.failedLine3'),
            ],
        },
        RE_COMPLETED: {
            title: t('learning.moduleQuizCompleted.recompletedTitle'),
            lines: [
                t('learning.moduleQuizCompleted.recompletedLine1', { name: learnerName }),
                t('learning.moduleQuizCompleted.recompletedLine2', { module: moduleTitle }),
            ],
        },
    }

    const { title, lines } = messages[state]

    return (
        <div className="mb-8 text-center">
            <h1 className="mb-3 text-3xl font-bold text-gray-900">
                {title}
            </h1>
            <div className="space-y-1 text-lg text-gray-600">
                {lines.map((line, i) => (
                    <p key={i}>{i === 1 ? <ContentMetadataText text={line} /> : line}</p>
                ))}
            </div>
        </div>
    )
}

interface ResultsCardProps {
    currentSession: ModuleQuizCompletionSummaryResponse['currentSession']
    previousSession?: ModuleQuizCompletionSummaryResponse['previousSession']
    t: (key: string, opts?: Record<string, unknown>) => string
}

function ResultsCard({ currentSession, previousSession, t }: ResultsCardProps) {
    return (
        <div className="overflow-hidden mb-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="p-6">
                <h2 className="mb-4 text-lg font-bold text-gray-900">{t('learning.moduleQuizCompleted.results')}</h2>

                <div className="space-y-3">
                    {/* 이번 결과 */}
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                        <span className="font-medium text-gray-600">{t('learning.moduleQuizCompleted.currentResult')}</span>
                        <span className="text-gray-900">
                            <span className="text-lg font-bold">
                                {formatScore(currentSession.score)}
                            </span>
                            <span className="ml-2 text-sm text-gray-500">
                                / {formatDateTime(currentSession.completedAt)}
                            </span>
                        </span>
                    </div>

                    {/* 지난 결과 */}
                    <div className="flex justify-between items-center py-3">
                        <span className="font-medium text-gray-600">{t('learning.moduleQuizCompleted.previousResult')}</span>
                        {previousSession ? (
                            <span className="text-gray-900">
                                <span className="text-lg font-bold">
                                    {formatScore(previousSession.score)}
                                </span>
                                <span className="ml-2 text-sm text-gray-500">
                                    / {formatDateTime(previousSession.completedAt)}
                                </span>
                            </span>
                        ) : (
                            <span className="text-gray-400">-</span>
                        )}
                    </div>
                </div>

                {/* 정답 개수 표시 */}
                <div className="pt-4 mt-4 border-t border-gray-100">
                    <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">{t('learning.moduleQuizCompleted.correct')}</span>
                        <span className="font-semibold text-gray-700">
                            {t('learning.moduleQuizCompleted.correctCount', { correct: currentSession.correctCount, total: currentSession.totalQuestions })}
                        </span>
                    </div>
                    {/* Progress bar */}
                    <div className="overflow-hidden mt-2 h-2 bg-gray-200 rounded-full">
                        <div
                            className={cn(
                                'h-full rounded-full transition-all duration-500',
                                currentSession.passed ? 'bg-emerald-500' : 'bg-amber-500'
                            )}
                            style={{
                                width: `${(currentSession.correctCount / currentSession.totalQuestions) * 100}%`
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

interface ActionButtonsProps {
    state: ModuleQuizCompletionState
    summary: ModuleQuizCompletionSummaryResponse
    firstLessonId?: number
    onNextLesson: (lessonId: number) => void
    onReview: (lessonId: number) => void
    onRetry: () => void
    onBackToModule: () => void
    onCourseComplete: () => void
    t: (key: string, opts?: Record<string, unknown>) => string
}

function ActionButtons({
    state,
    summary,
    firstLessonId,
    onNextLesson,
    onReview,
    onRetry,
    onBackToModule,
    onCourseComplete,
    t,
}: ActionButtonsProps) {
    const hasNextLesson = !!summary.nextLesson

    // FAILED 상태
    if (state === 'FAILED') {
        return (
            <div className="space-y-4">
                {/* Primary Actions */}
                <div className="flex flex-col gap-3 justify-center sm:flex-row">
                    <button
                        onClick={() => firstLessonId && onReview(firstLessonId)}
                        disabled={!firstLessonId}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 font-semibold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <BookOpen className="w-5 h-5" />
                        <span>{t('learning.moduleQuizCompleted.review')}</span>
                    </button>

                    <button
                        onClick={onRetry}
                        className="flex gap-2 justify-center items-center px-6 py-3 font-semibold text-gray-700 bg-white rounded-xl border-2 border-gray-200 transition-all hover:bg-gray-50 hover:border-gray-300"
                    >
                        <RotateCcw className="w-5 h-5" />
                        <span>{t('learning.moduleQuizCompleted.retryQuiz')}</span>
                    </button>
                </div>

                {/* 강의 목록으로 */}
                <div className="flex justify-center">
                    <button
                        onClick={onBackToModule}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg font-medium transition-all hover:-translate-y-0.5"
                    >
                        <List className="w-4 h-4" />
                        <span>{t('learning.moduleQuizCompleted.toCourseList')}</span>
                    </button>
                </div>
            </div>
        )
    }

    // PASSED or RE_COMPLETED 상태
    return (
        <div className="space-y-4">
            {/* Primary Actions */}
            <div className="flex flex-col gap-3 justify-center sm:flex-row">
                <button
                    onClick={onBackToModule}
                    className="flex gap-2 justify-center items-center px-6 py-3 font-semibold text-gray-700 bg-white rounded-xl border-2 border-gray-200 shadow-sm transition-all hover:bg-gray-50 hover:border-gray-300 hover:shadow-md hover:-translate-y-0.5"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span>{t('learning.moduleQuizCompleted.toCourseList')}</span>
                </button>

                {
                    hasNextLesson && import.meta.env.DEV ? (
                        <button
                            onClick={() => onNextLesson(summary.nextLesson!.lessonId)}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 font-semibold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                        >
                            <span>{t('learning.moduleQuizCompleted.startNextLesson')}</span>
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    ) : summary.isCourseCompleted ? (
                        <button
                            onClick={onCourseComplete}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-semibold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                        >
                            <Trophy className="w-5 h-5" />
                            <span>{t('learning.moduleQuizCompleted.confirmComplete')}</span>
                        </button>
                    ) : firstLessonId ? (
                        <button
                            onClick={() => onReview(firstLessonId)}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 font-semibold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                        >
                            <BookOpen className="w-5 h-5" />
                            <span>{t('learning.moduleQuizCompleted.review')}</span>
                        </button>
                    ) : null
                }
            </div>
        </div>
    )
}
